'use client'

import type { CSSProperties } from 'react'
import React from 'react'
import { cn } from '@/lib/utils'
import {
  formErrorSurfaceStyle,
  formLabelStyle,
  formTokenClassNames,
  formTokens,
} from '@/lib/design/formTokens'

/**
 * Dark-glass field label — Profile V2 eyebrow geometry (11px / 0.14em) via formTokens.
 * Prefer this over WagamourV2ModalFieldLabel until that primitive is aligned (deferred debt).
 */
export function FormFieldLabel({
  className,
  style,
  children,
  ...props
}: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={cn('mb-1 block', formTokenClassNames.label, className)}
      style={{ ...formLabelStyle(), ...style }}
      {...props}
    >
      {children}
    </label>
  )
}

/** Required-field cue — warm champagne, never light red utilities. */
export function FormFieldRequiredMark({
  className,
  style,
}: {
  className?: string
  style?: CSSProperties
}) {
  return (
    <span
      className={className}
      style={{ color: formTokens.darkGlass.required, ...style }}
      aria-hidden
    >
      *
    </span>
  )
}

/** Inline field validation message — shared destructive foreground. */
export function FormFieldError({
  children,
  className,
  id,
}: {
  children: React.ReactNode
  className?: string
  id?: string
}) {
  if (children == null || children === false) return null
  return (
    <p
      id={id}
      role="alert"
      className={cn('mt-1 text-xs', className)}
      style={{ color: formTokens.darkGlass.error }}
    >
      {children}
    </p>
  )
}

/** Form-level error banner — glass-safe surface, never light solid red boxes. */
export function FormErrorBanner({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  if (children == null || children === false) return null
  return (
    <div
      role="alert"
      className={cn(
        'rounded-[16px] px-3 py-2.5 text-sm leading-relaxed',
        className
      )}
      style={formErrorSurfaceStyle()}
    >
      {children}
    </div>
  )
}
